import type { ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import type { OrderForm } from 'src/sdk/checkout/entity/orderForm'

import { DisplayToasts } from '.'
import { appendToast } from './appendToast'

interface ToastContextProps {
  sendToast: (newToast: SendedToast) => void
  removeToast: (removedToast: Toast) => void
}

type ToastVariant =
  | 'success'
  | 'error'
  | 'warning'
  | 'abandoned'
  | 'pixPaymentReminder'

export type Toast = {
  readonly id?: string
  text?: string
  subtext?: string | ReactNode
  image?: string
  variant: ToastVariant
  persist?: boolean
  orderFormInfo?: OrderForm
  toastStartTime?: number
  onClose?: (reason: 'clicked_x') => void
}

type SendedToast = Omit<Toast, 'id'>

const ToastContext = createContext<ToastContextProps>({} as ToastContextProps)

const TOAST_DELAY_MS = 8000

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([])

  useEffect(() => {
    if (toasts.length === 0 || toasts[0].persist) {
      return undefined
    }

    const timer = setTimeout(() => {
      setToasts((prevToasts) => prevToasts.slice(1))
    }, TOAST_DELAY_MS)

    return () => {
      clearTimeout(timer)
    }
  }, [toasts])

  const sendToast = useCallback((newToast: SendedToast) => {
    const newToastWithId = { id: crypto.randomUUID(), ...newToast }

    setToasts((prevToasts) => appendToast(prevToasts, newToastWithId))
  }, [])

  const removeToast = useCallback((removedToast: Toast) => {
    if (removedToast.onClose) {
      removedToast.onClose('clicked_x')
    }

    setToasts((prevToasts) =>
      prevToasts.filter((toast) => toast.id !== removedToast.id)
    )
  }, [])

  return (
    <ToastContext.Provider value={{ sendToast, removeToast }}>
      <DisplayToasts toasts={toasts} />
      {children}
    </ToastContext.Provider>
  )
}

export const useToastContext = () => useContext(ToastContext)
